import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useDebounced } from "../hooks/index.ts"
import { useI18n } from "../i18n/index.tsx"
import { MANUAL_CATEGORIES, MANUAL_DOCS } from "../manual/index.ts"
import type { Block, ManualDoc } from "../manual/types.ts"
import {
  findDoc,
  flatDocs,
  groupDocs,
  neighbors,
  searchDocs,
  type Locale,
} from "../lib/manual.ts"
import { Badge, Button, Icon, Input, PageHeader } from "../ui/index.ts"

const SEARCH_DEBOUNCE_MS = 200

/** 문서 한 조각을 그린다. 새 종류를 더하면 여기에만 한 줄 는다. */
function BlockView({ block, locale }: { block: Block; locale: Locale }) {
  switch (block.kind) {
    case "heading":
      return <h2 className="doc__h2">{block.text[locale]}</h2>
    case "p":
      return <p className="doc__p">{block.text[locale]}</p>
    case "list":
      return (
        <ul className="doc__list">
          {block.items.map((item, index) => (
            <li key={index}>{item[locale]}</li>
          ))}
        </ul>
      )
    case "steps":
      return (
        <ol className="doc__steps">
          {block.items.map((item, index) => (
            <li key={index}>{item[locale]}</li>
          ))}
        </ol>
      )
    case "table":
      return (
        <div className="table-wrap doc__table">
          <table className="table table--compact">
            <thead>
              <tr>
                {block.head.map((cell, index) => (
                  <th key={index} scope="col">
                    {cell[locale]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{cell[locale]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    case "note":
      return (
        <div className={`doc__note doc__note--${block.tone}`}>
          <Icon name={block.tone === "info" ? "info" : "alert"} size={16} />
          <span>{block.text[locale]}</span>
        </div>
      )
    case "image":
      return (
        <figure className="doc__figure">
          <img src={block.src[locale]} alt={block.alt[locale]} loading="lazy" />
          {block.caption ? <figcaption>{block.caption[locale]}</figcaption> : null}
        </figure>
      )
  }
}

/**
 * 사용자 매뉴얼.
 *
 * 왼쪽에 목차, 오른쪽에 고른 문서 하나. 내용은 `manual/` 의 데이터에서 오고
 * 찾기·앞뒤 문서 판단은 `lib/manual.ts` 가 한다 — 이 파일은 그리기만 한다.
 */
export function ManualPage() {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const { slug } = useParams()

  const [query, setQuery] = useState("")
  const settled = useDebounced(query.trim(), SEARCH_DEBOUNCE_MS)
  const [tocOpen, setTocOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const ordered = useMemo(() => flatDocs(MANUAL_CATEGORIES, MANUAL_DOCS), [])
  const groups = useMemo(() => groupDocs(MANUAL_CATEGORIES, MANUAL_DOCS), [])
  const hits = useMemo(() => searchDocs(MANUAL_DOCS, settled, locale), [settled, locale])

  const doc = findDoc(ordered, slug)
  const { prev, next } = neighbors(ordered, doc?.slug ?? "")

  // 분류는 접었다 편다. 지금 보고 있는 문서의 분류는 늘 펴 둔다.
  const [collapsed, setCollapsed] = useState<string[]>([])
  const isOpen = (category: string) =>
    category === doc?.category || !collapsed.includes(category)

  // 문서를 바꾸면 본문을 맨 위로 — 아래를 읽다가 넘어가면 새 문서 중간부터 보인다.
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 })
    window.scrollTo({ top: 0 })
    setTocOpen(false)
  }, [slug])

  function open(next: ManualDoc) {
    void navigate(`/manual/${next.slug}`)
  }

  const toc = (
    <nav className="manual__toc" aria-label={t("manual.toc")}>
      {settled !== "" ? (
        <div className="manual__results">
          <div className="manual__results-head">
            {t("manual.results", { count: hits.length })}
          </div>
          {hits.length === 0 ? (
            <p className="manual__empty">{t("manual.noResults")}</p>
          ) : (
            hits.map((hit) => (
              <button
                key={hit.doc.slug}
                type="button"
                className={hit.doc.slug === doc?.slug ? "manual__hit is-active" : "manual__hit"}
                onClick={() => open(hit.doc)}
              >
                <strong>{hit.doc.title[locale]}</strong>
                <span className="manual__snippet">{hit.snippet}</span>
              </button>
            ))
          )}
        </div>
      ) : (
        groups.map((group) => (
          <div className="manual__group" key={group.category.slug}>
            <button
              type="button"
              className="manual__group-head"
              aria-expanded={isOpen(group.category.slug)}
              onClick={() =>
                setCollapsed((current) =>
                  current.includes(group.category.slug)
                    ? current.filter((slug) => slug !== group.category.slug)
                    : [...current, group.category.slug],
                )
              }
            >
              <Icon
                name={isOpen(group.category.slug) ? "chevronDown" : "chevronRight"}
                size={13}
              />
              {group.category.title[locale]}
            </button>
            {isOpen(group.category.slug) ? (
              <div className="manual__links">
                {group.docs.map((item) => (
                  <Link
                    key={item.slug}
                    to={`/manual/${item.slug}`}
                    className={item.slug === doc?.slug ? "manual__link is-active" : "manual__link"}
                    aria-current={item.slug === doc?.slug ? "page" : undefined}
                  >
                    {item.title[locale]}
                  </Link>
                ))}
              </div>
            ) : null}
          </div>
        ))
      )}
    </nav>
  )

  return (
    <>
      <PageHeader
        title={t("manual.title")}
        description={t("manual.description")}
        actions={
          <div className="manual__search">
            <Input
              icon="search"
              value={query}
              placeholder={t("manual.searchPlaceholder")}
              aria-label={t("manual.search")}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        }
      />

      {/* 좁은 화면에서는 목차를 접어 둔다. */}
      <Button
        className="manual__toc-toggle"
        icon="list"
        onClick={() => setTocOpen((open) => !open)}
      >
        {t("manual.toc")}
      </Button>

      <div className="manual" data-toc-open={tocOpen ? "true" : "false"}>
        {toc}

        <article className="manual__content" ref={contentRef}>
          {doc === null ? (
            <p className="manual__empty">{t("manual.noResults")}</p>
          ) : (
            <>
              <header className="doc__head">
                <div className="doc__eyebrow">
                  {MANUAL_CATEGORIES.find((category) => category.slug === doc.category)?.title[
                    locale
                  ] ?? ""}
                </div>
                <h1 className="doc__title">{doc.title[locale]}</h1>
                <p className="doc__summary">{doc.summary[locale]}</p>
                {doc.roles ? (
                  <div className="doc__roles">
                    {doc.roles.map((role) => (
                      <Badge key={role} tone="neutral" size="sm">
                        {t(`role.${role}`)}
                      </Badge>
                    ))}
                  </div>
                ) : null}
              </header>

              {doc.body.map((block, index) => (
                <BlockView key={index} block={block} locale={locale} />
              ))}

              <nav className="doc__nav" aria-label={t("manual.toc")}>
                {prev ? (
                  <Link to={`/manual/${prev.slug}`} className="doc__nav-link">
                    <span className="doc__nav-label">
                      <Icon name="chevronLeft" size={13} /> {t("manual.prev")}
                    </span>
                    <strong>{prev.title[locale]}</strong>
                  </Link>
                ) : (
                  <span />
                )}
                {next ? (
                  <Link to={`/manual/${next.slug}`} className="doc__nav-link doc__nav-link--next">
                    <span className="doc__nav-label">
                      {t("manual.next")} <Icon name="chevronRight" size={13} />
                    </span>
                    <strong>{next.title[locale]}</strong>
                  </Link>
                ) : (
                  <span />
                )}
              </nav>
            </>
          )}
        </article>
      </div>
    </>
  )
}
